

import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { CertificationProgram } from '../types';

const CertificationPrograms: React.FC = () => {
  const certifications: CertificationProgram[] = [
  {
    id: '1',
    title: 'camlytix AI Foundation Certification',
    description: 'Master the fundamentals of camlytix AI deployment, configuration, and basic analytics implementation. Perfect for getting started with the platform.',
    level: 'foundation',
    duration: '4 weeks',
    modules: 8,
    image: "https://images.unsplash.com/photo-1657062720986-ab38491ce255",
    imageAlt: 'Open laptop on wooden desk with notebook and coffee cup showing online learning environment',
    enrollmentStatus: 'available'
  },
  {
    id: '2',
    title: 'Professional Analytics Implementation Specialist',
    description: 'Advanced training in complex multi-camera deployments, custom analytics configuration, and enterprise-scale implementations with performance optimization.',
    level: 'professional',
    duration: '8 weeks',
    modules: 12,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b810f2b0-1764487581869.png",
    imageAlt: 'Professional training session with instructor presenting to engaged students in modern classroom',
    enrollmentStatus: 'enrolled'
  },
  {
    id: '3',
    title: 'Expert System Architect Certification',
    description: 'Comprehensive mastery of camlytix AI architecture, API integration, custom model development, and enterprise solution design for large-scale deployments.',
    level: 'expert',
    duration: '12 weeks',
    modules: 16,
    image: "https://images.unsplash.com/photo-1531537264351-c1952d1db1f5",
    imageAlt: 'Team of professionals collaborating around computer screens in modern tech office environment',
    enrollmentStatus: 'available'
  }];


  const getLevelColor = (level: CertificationProgram['level']) => {
    switch (level) {
      case 'foundation':
        return 'bg-success/10 text-success border-success/30';
      case 'professional':
        return 'bg-primary/10 text-primary border-primary/30';
      case 'expert':
        return 'bg-insight-purple/10 text-insight-purple border-insight-purple/30';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getLevelIcon = (level: CertificationProgram['level']) => {
    switch (level) {
      case 'foundation':
        return 'BookOpen';
      case 'professional':
        return 'Award';
      case 'expert':
        return 'Trophy';
      default:
        return 'Award';
    }
  };

  const getEnrollmentButton = (status: CertificationProgram['enrollmentStatus']) => {
    switch (status) {
      case 'enrolled':
        return (
          <Button
            variant="outline"
            size="default"
            iconName="Play"
            iconPosition="left"
            onClick={() => console.log('Continue learning')}>

            Continue Learning
          </Button>);

      case 'completed':
        return (
          <Button
            variant="outline"
            size="default"
            iconName="CheckCircle2"
            iconPosition="left"
            disabled>

            Completed
          </Button>);

      default:
        return (
          <Button
            variant="default"
            size="default"
            iconName="BookOpen"
            iconPosition="left"
            onClick={() => console.log('Enroll now')}>

            Enroll Now
          </Button>);

    }
  };

  return (
    <section className="py-16 px-4 md:px-6 lg:px-8 bg-surface">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Analytics Academy
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Become a certified camlytix AI expert through our comprehensive training programs. Gain industry-recognized credentials and advance your career.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {certifications.map((cert) =>
          <div
            key={cert.id}
            className="bg-card rounded-2xl border border-border overflow-hidden hover:shadow-brand-lg transition-all duration-300 group">

              <div className="relative h-48 overflow-hidden">
                <Image
                src={cert.image}
                alt={cert.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4">
                  <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border ${getLevelColor(cert.level)}`}>
                    <Icon name={getLevelIcon(cert.level) as any} size={16} />
                    <span className="text-xs font-semibold uppercase tracking-wide">
                      {cert.level}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                  {cert.title}
                </h3>

                <p className="text-sm text-text-secondary mb-4 line-clamp-3">
                  {cert.description}
                </p>

                <div className="flex items-center space-x-4 mb-6 text-sm text-text-secondary">
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>{cert.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="BookOpen" size={16} />
                    <span>{cert.modules} modules</span>
                  </div>
                </div>

                {getEnrollmentButton(cert.enrollmentStatus)}
              </div>
            </div>
          )}
        </div>

        <div className="mt-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl border border-primary/20 p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Corporate Training Programs
              </h3>
              <p className="text-text-secondary mb-6">
                Customized training solutions for enterprise teams. Get your entire organization certified with our tailored corporate programs including on-site training, dedicated support, and flexible scheduling.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Customized curriculum based on your use cases</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">On-site or virtual training options available</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Dedicated account manager and support team</span>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircle2" size={20} className="text-success flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground">Volume discounts for team enrollments</span>
                </div>
              </div>
              <Button
                variant="default"
                size="lg"
                iconName="Users"
                iconPosition="left"
                onClick={() => console.log('Contact for corporate training')}>

                Contact for Corporate Training
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Users" size={32} className="text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">2,500+</div>
                <div className="text-sm text-text-secondary">Certified Professionals</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Building2" size={32} className="text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">150+</div>
                <div className="text-sm text-text-secondary">Enterprise Clients</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Award" size={32} className="text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">98%</div>
                <div className="text-sm text-text-secondary">Completion Rate</div>
              </div>
              <div className="bg-background/50 backdrop-blur-sm rounded-xl p-6 text-center">
                <Icon name="Star" size={32} className="text-primary mx-auto mb-3" />
                <div className="text-3xl font-bold text-foreground mb-1">4.9/5</div>
                <div className="text-sm text-text-secondary">Average Rating</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CertificationPrograms;